/* Simplified fork from v1.3.0 https://github.com/Emiliano-Bucci/react-spring-carousel-js */
/* eslint-disable complexity */
import React, { createContext, useCallback, useContext, useEffect, useRef, useState, } from 'react';
import { config, useSpring } from '@react-spring/web';
import { useDrag } from '@use-gesture/react';
import { AnimatedSpringCarouselWrapper, SpringCarouselItemWrapper, SpringCarouselWrapper, TermsAndConditions, } from '../components/Slider/styled-components';
import { useThumbsModule } from './useThumbsModule';
import { fixNegativeIndex, useMount } from './utils';
const UseSpringCarouselContext = createContext(undefined);
export function useSpringCarouselContext() {
    const context = useContext(UseSpringCarouselContext);
    if (!context) {
        throw new Error(`useSpringCarouselContext isn't being used within the useSpringCarousel context;
    use the context only inside a component that is rendered within the Carousel.`);
    }
    return context;
}
export function useSpringCarousel({ items, withLoop = false, draggingSlideThreshold = 100, springConfig = config.default, shouldResizeOnWindowResize = true, withThumbs = false, itemsPerSlide = 1, initialActiveItem = 0, disableGestures = false, onSetup, onSlideChange, }) {
    function getRepeatedPrevItems() {
        const total = items.length;
        return items.slice(total - itemsPerSlide - 1, total);
    }
    function getRepeatedNextItems() {
        return items.slice(0, itemsPerSlide + 1);
    }
    function getItems() {
        if (withLoop) {
            return [...getRepeatedPrevItems(), ...items, ...getRepeatedNextItems()];
        }
        return items;
    }
    const slideActionType = useRef('next');
    const internalItems = getItems();
    const [activeItemIndex, setActiveItemIndex] = useState(initialActiveItem);
    const [windowWidth, setWidowWidth] = useState(0);
    const mainCarouselWrapperRef = useRef(null);
    const carouselTrackWrapperRef = useRef(null);
    const [isDragging, setIsDragging] = useState(false);
    const isAnimating = useRef(false);
    useEffect(() => {
        if (windowWidth) {
            setCarouselStyles.current[0].start({
                x: -(getSlideValue() * getCurrentActiveItem()),
                immediate: true,
            });
        }
    }, [windowWidth]);
    const { thumbsFragment: thumbsFragmentComponent } = useThumbsModule({
        withThumbs,
        items,
        springConfig,
    });
    const [carouselStyles, setCarouselStyles] = useSpring(() => ({
        x: 0,
        config: springConfig,
    }));
    const bindDrag = useDrag((props) => {
        const { dragging } = props;
        const movement = props.movement[0];
        if (getIsAnimating()) {
            return;
        }
        const currentSlidedValue = -(getSlideValue() * getCurrentActiveItem());
        if (dragging) {
            setCarouselStyles.current[0].start({
                x: currentSlidedValue + movement,
            });
            // onDrag EVENT
            setIsDragging(true);
            const prevItemThreshold = movement > draggingSlideThreshold;
            const nextItemThreshold = movement < -draggingSlideThreshold;
            if (nextItemThreshold) {
                if (!withLoop &&
                    getCurrentActiveItem() === internalItems.length - 1) {
                    setCarouselStyles.current[0].start({
                        x: currentSlidedValue,
                    });
                }
                else {
                    slideToNextItem();
                }
                props.cancel();
                setIsDragging(false);
            }
            else if (prevItemThreshold) {
                if (!withLoop && getCurrentActiveItem() === 0) {
                    setCarouselStyles.current[0].start({
                        x: currentSlidedValue,
                    });
                }
                else {
                    slideToPrevItem();
                }
                props.cancel();
                setIsDragging(false);
            }
        }
        if (props.last && !getIsAnimating()) {
            setCarouselStyles.current[0].start({
                x: currentSlidedValue,
            });
        }
    }, {
        enabled: !disableGestures,
    });
    // Perform some check on first mount
    useMount(() => {
        if (itemsPerSlide > items.length) {
            throw new Error("The itemsPerSlide prop can't be greater than the total length of the items you provide.");
        }
        if (!shouldResizeOnWindowResize) {
            console.warn('You set shouldResizeOnWindowResize={false}');
        }
        if (initialActiveItem < 0) {
            console.warn('The initialActiveItem cannot be less than 0.');
        }
        if (initialActiveItem > items.length) {
            console.warn('The initialActiveItem cannot be greater than the total length of the items you provide.');
        }
        // onSetup EVENT
        if (onSetup)
            onSetup();
    });
    useMount(() => {
        if (shouldResizeOnWindowResize) {
            window.addEventListener('resize', handleResize);
            return () => {
                window.removeEventListener('resize', handleResize);
            };
        }
        return () => undefined;
    });
    useMount(() => {
        if (initialActiveItem > 0 && initialActiveItem <= items.length) {
            slideToItem({
                item: initialActiveItem,
                immediate: true,
            });
            setActiveItemIndex(initialActiveItem);
        }
    });
    const getSlideValue = useCallback(() => {
        if (!carouselTrackWrapperRef.current) {
            return 0;
        }
        const carouselItem = carouselTrackWrapperRef.current
            .firstChild;
        return carouselItem.getBoundingClientRect().width;
    }, []);
    function handleResize() {
        setWidowWidth(window.innerWidth);
    }
    function setSlideActionType(type) {
        slideActionType.current = type;
    }
    function getCurrentActiveItem() {
        return activeItemIndex;
    }
    function getIsAnimating() {
        return isAnimating.current;
    }
    function setIsAnimating(val) {
        isAnimating.current = val;
    }
    function getPrevItem() {
        const currentActiveItem = getCurrentActiveItem();
        if (currentActiveItem === 0) {
            return items.length - 1;
        }
        return currentActiveItem - 1;
    }
    function getNextItem() {
        const currentActiveItem = getCurrentActiveItem();
        if (currentActiveItem === items.length - 1) {
            return 0;
        }
        return currentActiveItem + 1;
    }
    function getIsNextItem(id) {
        const itemIndex = findItemIndex(id);
        const currentActiveItem = getCurrentActiveItem();
        if (withLoop && currentActiveItem === items.length - 1) {
            return itemIndex === 0;
        }
        return itemIndex === currentActiveItem + 1;
    }
    function getIsPrevItem(id) {
        const itemIndex = findItemIndex(id);
        const currentActiveItem = getCurrentActiveItem();
        if (withLoop && currentActiveItem === 0) {
            return itemIndex === items.length - 1;
        }
        return itemIndex === currentActiveItem - 1;
    }
    function findItemIndex(id) {
        return items.findIndex((item) => item.id === id);
    }
    function slideToItem({ from, item, immediate = false, onRest = () => { }, }) {
        const nextItemIndex = fixNegativeIndex(item, items.length);
        // Slide Change Event
        if (onSlideChange)
            onSlideChange(nextItemIndex);
        if (!immediate) {
            setActiveItemIndex(nextItemIndex);
            setIsAnimating(true);
            // onSlideStartChange EVENT
        }
        setCarouselStyles.current[0].start(Object.assign(Object.assign({}, (from
            ? {
                from: {
                    x: from,
                },
            }
            : {})), { to: {
                x: -(getSlideValue() * item),
            }, config: Object.assign({}, springConfig), immediate, onRest: (val) => {
                if (val.finished) {
                    setIsDragging(false);
                    setIsAnimating(false);
                    onRest();
                }
            } }));
    }
    function getWrapperFromValue(element) {
        const values = element.style.transform.split(/\w+\(|\);?/);
        return Number(values[1].split(/,\s?/g)[0].replace('px', ''));
    }
    function slideToPrevItem() {
        if ((!withLoop && getCurrentActiveItem() === 0) ||
            (isDragging && getIsAnimating())) {
            return;
        }
        setSlideActionType('prev');
        if (withLoop && getCurrentActiveItem() === 0) {
            if (isDragging) {
                slideToItem({
                    item: activeItemIndex - 1,
                    onRest: () => {
                        slideToItem({
                            item: items.length - 1,
                            immediate: true,
                        });
                    },
                });
            }
            else {
                let fromValue = 0;
                if (carouselTrackWrapperRef.current.style.transform !== 'none') {
                    fromValue = getWrapperFromValue(carouselTrackWrapperRef.current);
                }
                slideToItem({
                    from: -(Math.abs(fromValue) + getSlideValue() * items.length),
                    item: items.length - 1,
                });
            }
        }
        else {
            slideToItem({
                item: getPrevItem(),
            });
        }
    }
    function slideToNextItem() {
        if ((!withLoop && getCurrentActiveItem() === internalItems.length - 1) ||
            (isDragging && getIsAnimating())) {
            return;
        }
        setSlideActionType('next');
        if (withLoop && getCurrentActiveItem() === items.length - 1) {
            if (isDragging) {
                slideToItem({
                    item: activeItemIndex + 1,
                    onRest: () => {
                        setActiveItemIndex(0);
                        slideToItem({
                            item: 0,
                            immediate: true,
                        });
                    },
                });
            }
            else {
                slideToItem({
                    from: getWrapperFromValue(carouselTrackWrapperRef.current) +
                        getSlideValue() * items.length,
                    item: 0,
                });
            }
        }
        else {
            slideToItem({
                item: getNextItem(),
            });
        }
    }
    function internalSlideToItem(item) {
        let itemIndex;
        if (typeof item === 'string') {
            itemIndex = items.findIndex((_item) => _item.id === item);
        }
        else {
            itemIndex = item;
        }
        if (itemIndex >= items.length) {
            throw Error(`The item you want to slide to doesn't exist. This could be due to the fact that
        you provide a wrong id or a higher numeric index.`);
        }
        if (itemIndex === getCurrentActiveItem()) {
            return;
        }
        const currentItem = findItemIndex(items[getCurrentActiveItem()].id);
        const newActiveItem = findItemIndex(items[itemIndex].id);
        if (newActiveItem > currentItem) {
            setSlideActionType('next');
        }
        else {
            setSlideActionType('prev');
        }
        slideToItem({
            item: itemIndex,
        });
    }
    const contextProps = {
        isDragging,
        getIsAnimating,
        getIsNextItem,
        getIsPrevItem,
        slideToPrevItem,
        slideToNextItem,
        slideToItem: internalSlideToItem,
        getCurrentActiveItem: () => ({
            id: items[getCurrentActiveItem()].id,
            index: getCurrentActiveItem(),
        }),
        getIsActiveItem: (id) => findItemIndex(id) === getCurrentActiveItem(),
    };
    const carouselFragment = (React.createElement(UseSpringCarouselContext.Provider, { value: contextProps },
        React.createElement(SpringCarouselWrapper, { ref: mainCarouselWrapperRef, "data-testid": "use-spring-carousel-wrapper" },
            React.createElement(AnimatedSpringCarouselWrapper, Object.assign({}, bindDrag(), { "data-testid": "use-spring-carousel-animated-wrapper", style: carouselStyles, ref: (ref) => {
                    if (ref) {
                        carouselTrackWrapperRef.current = ref;
                    }
                } }), internalItems.map(({ id, renderItem, termsAndConditions }, index) => (React.createElement(SpringCarouselItemWrapper, { key: `${id}-${index}`, "data-testid": "use-spring-carousel-item-wrapper", itemsPerSlide: itemsPerSlide, role: "tabpanel", "aria-hidden": index !== activeItemIndex, "aria-labelledby": `thumb-${id}`, tabIndex: -1 },
                renderItem,
                termsAndConditions && (React.createElement(TermsAndConditions, { "data-testid": "use-spring-carousel-terms-and-conditions", role: "link", "aria-labelledby": `terms-and-conditions-${id}`, href: termsAndConditions.url }, termsAndConditions.label)))))))));
    const thumbsFragment = (React.createElement(UseSpringCarouselContext.Provider, { value: contextProps }, thumbsFragmentComponent));
    return Object.assign({ carouselFragment,
        thumbsFragment }, contextProps);
}
